export type ServicePage = {
  slug: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  city?: string;
  sections: { heading: string; body: string }[];
  bullets: string[];
  faq: { question: string; answer: string }[];
};

const sharedBullets = [
  "Wij komen naar jouw locatie: kantoor, sporthal, feestzaal of eigen terrein",
  "Free-roam VR: geen kabels, geen controllers-gedoe, gewoon vrij bewegen",
  "Geschikt voor 2 tot 6 spelers per sessie, groepen tot ~40 personen per dagdeel",
  "Begeleiding en volledige set-up door onze crew inbegrepen",
  "Ook voor mensen die nog nooit VR hebben gedaan: uitleg in 3 minuten",
];

const sharedFaq = [
  {
    question: "Hoeveel ruimte is er nodig voor VR op locatie?",
    answer:
      "Voor een free-roam opstelling gebruiken we bij voorkeur een ruimte van circa 8 x 8 meter met een vrije hoogte van 2,5 meter. Kleinere ruimtes zijn mogelijk, we stemmen de games daar dan op af.",
  },
  {
    question: "Wat kost een VR bedrijfsuitje?",
    answer:
      "De prijs hangt af van groepsgrootte, duur en locatie. Vraag een vrijblijvende prijsindicatie aan via het contactformulier; je ontvangt doorgaans binnen één werkdag een voorstel op maat.",
  },
  {
    question: "Hoe lang duurt een sessie?",
    answer:
      "Een sessie duurt gemiddeld 20 tot 30 minuten per groepje. Een compleet programma voor een team van 20 personen plannen we meestal in 2 tot 3 uur inclusief wissels.",
  },
  {
    question: "Is VR geschikt voor iedereen in het team?",
    answer:
      "Ja. Onze games zijn zo gekozen dat ze zonder game-ervaring te spelen zijn en niemand hoeft mee te doen om onderdeel van de beleving te zijn: meekijken en meebeslissen hoort erbij.",
  },
];

export const servicePages: ServicePage[] = [
  {
    slug: "vr-teamuitje",
    h1: "VR teamuitje op locatie naar keuze",
    title: "VR Teamuitje op Locatie | Kies je Eigen Plek – PlayfieldVR",
    description:
      "Organiseer een VR-teamuitje op de locatie van jouw keuze. Wij regelen opbouw en begeleiding. Vanaf € 20,- p.p. Vraag een voorstel aan.",
    intro:
      "Op zoek naar een teamuitje dat niet aanvoelt als een verplichte activiteit? Bij een VR teamuitje van PlayfieldVR bouwen wij een complete free-roam virtual reality arena op de locatie van jouw keuze. Je team loopt samen echt door een virtuele wereld: geen kabels, geen simulator-stoelen, gewoon met elkaar door een spel bewegen.",
    sections: [
      {
        heading: "Virtual reality met je team, niet naast elkaar",
        body: "Bij veel VR-activiteiten zit iedereen los in zijn eigen headset. Bij ons staat het hele groepje in dezelfde virtuele ruimte: je ziet elkaar, je hoort elkaar en je moet samen keuzes maken. Dat maakt het een teamactiviteit in plaats van een reeks individuele beurten.",
      },
      {
        heading: "Wat een teamuitje met VR oplevert",
        body: "Onder tijdsdruk in een onbekende wereld ontstaat spontaan rolverdeling: wie overziet het geheel, wie let op details, wie durft als eerste te bewegen. Teams praten er nog weken over na, en het is een makkelijk gespreksonderwerp voor collega's die elkaar nog niet goed kennen.",
      },
      {
        heading: "Plannen rond jullie agenda",
        body: "Een VR teamuitje past in een middag, een avond of als onderdeel van een grotere teamdag. Wij komen ruim op tijd opbouwen en ruimen alles weer op, zodat de rest van jullie programma ongestoord doorgaat.",
      },
    ],
    bullets: sharedBullets,
    faq: [
      {
        question: "Wat is een VR teamuitje precies?",
        answer:
          "Een VR teamuitje is een teamactiviteit waarbij je met je collega's samen in een virtuele wereld speelt. Wij komen naar jullie locatie, bouwen een free-roam arena op en begeleiden de sessies, zodat het team alleen hoeft te spelen.",
      },
      ...sharedFaq,
    ],
  },
  {
    slug: "vr-borrelactiviteit-kantoor",
    h1: "Borrelactiviteit op kantoor met VR",
    title: "VR Borrelactiviteit op Kantoor | PlayfieldVR",
    description:
      "Verras je collega's tijdens de borrel met een VR-activiteit op kantoor. Snel opgebouwd, voor iedereen toegankelijk. Vraag een voorstel aan.",
    intro:
      "Een borrel op kantoor is gezellig, maar na een uur staan dezelfde mensen bij dezelfde mensen. Met een VR-opstelling naast de bar krijgt de vrijmibo een middelpunt: groepjes spelen om beurten, de rest kijkt live mee op het scherm en juicht mee. Wij bouwen alles op in jullie kantoor, kantine of eventruimte.",
    sections: [
      {
        heading: "Waarom VR werkt bij een borrel",
        body: "Een borrelactiviteit moet laagdrempelig zijn: je moet ertussenuit kunnen stappen, met een drankje in de hand kunnen blijven kijken en niet 20 minuten uitleg nodig hebben. Een sessie duurt kort, de uitleg is drie minuten en het meekijken is minstens zo leuk als het spelen.",
      },
      {
        heading: "Op jullie eigen kantoor",
        body: "Een leeg kantoordeel, de kantine of een vergaderruimte met de tafels aan de kant is meestal al genoeg. We checken vooraf de afmetingen en de vloer, en boren of plakken nergens iets vast.",
      },
      {
        heading: "Vrijmibo, jubileum of afscheid",
        body: "Naast de vrijdagmiddagborrel zetten we ook op bij jubilea, afscheidsborrels, nieuwjaarsborrels en klantenavonden. De speelduur en het aantal headsets stemmen we af op de groepsgrootte.",
      },
    ],
    bullets: sharedBullets,
    faq: [
      {
        question: "Kan VR ook als korte borrelactiviteit op kantoor?",
        answer:
          "Ja. We plannen korte rondes van 10 tot 20 minuten per groepje, zodat mensen kunnen aanhaken en afhaken wanneer het hen uitkomt. De opstelling staat gewoon naast de borrel.",
      },
      ...sharedFaq,
    ],
  },
  {
    slug: "vr-personeelsuitje",
    h1: "VR personeelsuitje voor het hele bedrijf",
    title: "VR Personeelsuitje | Voor het Hele Bedrijf – PlayfieldVR",
    description:
      "Een VR-personeelsuitje waar iedereen aan meedoet, van gamer tot non-gamer. Landelijke dekking, binnen 24 uur een voorstel op maat.",
    intro:
      "Een personeelsuitje moet werken voor iedereen: van de collega die elke dag gamet tot degene die nog nooit een headset op heeft gehad. Daarom kiest PlayfieldVR titels die je binnen een paar minuten begrijpt en spelen we in groepjes, met de rest van het bedrijf als publiek. Wij komen naar de locatie van jullie keuze.",
    sections: [
      {
        heading: "Geschikt voor grotere groepen",
        body: "Met wisselende rondes van 2 tot 6 spelers draaien we per dagdeel groepen tot circa 40 personen. Voor grotere bedrijven verdelen we het programma over meerdere blokken of dagdelen.",
      },
      {
        heading: "Niemand staat aan de kant",
        body: "Wie niet wil spelen, kijkt live mee op het grote scherm en helpt vanaf de zijlijn. Juist dat samen roepen en aanwijzingen geven maakt het een activiteit voor de hele afdeling in plaats van voor een paar liefhebbers.",
      },
      {
        heading: "Combineren met eten en drinken",
        body: "Veel bedrijven combineren VR met een lunch, buffet of borrel. Wij stemmen het speelschema af op jullie catering en tijden.",
      },
    ],
    bullets: sharedBullets,
    faq: sharedFaq,
  },
  {

    slug: "vr-bedrijfsuitje",
    h1: "VR bedrijfsuitje op locatie in Nederland",
    title: "VR Bedrijfsuitje op Locatie in Nederland | PlayfieldVR",
    description:
      "Compleet verzorgd VR-bedrijfsuitje, waar dan ook in Nederland. Teambuilding die blijft hangen. Vraag vandaag een offerte aan.",
    intro:
      "PlayfieldVR verzorgt een compleet VR bedrijfsuitje op de locatie van jouw keuze. Wij bouwen een free-roam virtual reality arena op in jouw kantoor, sporthal of feestzaal, zodat je team samen door een virtuele wereld beweegt zonder kabels of ingewikkelde uitleg. Geen reistijd, geen gedoe met vervoer: de beleving komt naar jullie toe.",
    sections: [
      {
        heading: "Waarom een bedrijfsuitje met VR werkt",
        body: "Een VR bedrijfsuitje zet iedereen in dezelfde onbekende situatie. Hiërarchie valt weg, mensen moeten hardop communiceren en samen keuzes maken onder tijdsdruk. Precies daarom blijft het langer hangen dan een borrel of een bowlingavond, en zien collega's elkaar op een nieuwe manier samenwerken.",
      },
      {
        heading: "Hoe een dag eruitziet",
        body: "Onze crew arriveert ruim voor aanvang en bouwt de arena op. Daarna spelen groepjes van 2 tot 6 personen in wisselende rondes; wie niet speelt kijkt live mee op het scherm. Wij regelen instructie, hygiëne van de headsets en de afbouw. Jullie hoeven alleen de ruimte en het enthousiasme te leveren.",
      },
      {
        heading: "Voor welke gelegenheden",
        body: "Teamdagen, personeelsfeesten, kick-offs, beursstands, klantendagen, familiedagen en vrijmibo's met iets meer body. Ook als onderdeel van een langer programma: wij passen de speelduur aan op jullie planning.",
      },
    ],
    bullets: sharedBullets,
    faq: sharedFaq,
  },
  {
    slug: "vr-teambuilding-purmerend",
    city: "Purmerend",
    h1: "VR teambuilding in Purmerend",
    title: "VR Teambuilding Purmerend | Bedrijfsuitje op Locatie – PlayfieldVR",
    description:
      "VR teambuilding in Purmerend: wij komen naar jouw kantoor of zaal met een complete VR-opstelling. Vraag vrijblijvend een voorstel aan.",
    intro:
      "PlayfieldVR is gevestigd in de regio en komt met de volledige free-roam opstelling naar jouw locatie in Purmerend. Kantoor, sporthal, buurthuis of bedrijfshal: als er ruimte is, bouwen wij daar een virtuele wereld. Ideaal voor teams die iets willen doen dat niet in een zaaltje met flip-over eindigt.",
    sections: [
      {
        heading: "Teambuilding zonder reistijd",
        body: "Omdat wij naar Purmerend toe komen, hoeft niemand in de auto. Dat scheelt uren en maakt het makkelijk om een sessie in een gewone werkdag te plannen, bijvoorbeeld als afsluiting van de middag.",
      },
      {
        heading: "Wat je team leert",
        body: "In onze escape- en co-op games kom je alleen verder door te overleggen, taken te verdelen en elkaar te vertrouwen. Na afloop bespreken we kort wat er in het spel gebeurde: wie nam de leiding, wie zag het detail dat de rest miste.",
      },
      {
        heading: "Locaties in en rond Purmerend",
        body: "Wij spelen regelmatig in Purmerend, Volendam, Edam, Beemster en Waterland. Heb je zelf geen geschikte ruimte, dan denken we mee over een zaal in de buurt.",
      },
    ],
    bullets: sharedBullets,
    faq: sharedFaq,
  },
  {
    slug: "vr-teambuilding-zaandam",
    city: "Zaandam",
    h1: "VR teambuilding in Zaandam",
    title: "VR Teambuilding Zaandam | Bedrijfsuitje op Locatie – PlayfieldVR",
    description:
      "VR teambuilding in Zaandam: mobiele free-roam VR-arcade bij jou op locatie. Geschikt voor elk team. Vraag een prijsindicatie aan.",
    intro:
      "Voor teams in Zaandam en de Zaanstreek verzorgt PlayfieldVR een mobiele free-roam VR ervaring op locatie. Wij nemen headsets, tracking en begeleiding mee, jij kiest de ruimte. Binnen een uur staat er een arena waar collega's samen door een virtuele wereld lopen.",
    sections: [
      {
        heading: "Mobiele VR in de Zaanstreek",
        body: "Van kantoorpanden in Zaandam-Zuid tot bedrijfshallen langs de Zaan: onze opstelling is flexibel. We doen vooraf een korte check van de ruimte, zodat we op de dag zelf direct kunnen opbouwen.",
      },
      {
        heading: "Geschikt voor elk team",
        body: "Onze titels zijn gekozen op laagdrempeligheid. Ook collega's die nooit gamen zijn binnen een paar minuten binnen. Wie liever niet speelt, heeft een rol als coach vanaf de zijlijn.",
      },
      {
        heading: "Combineren met een programma",
        body: "Veel teams combineren VR met een lunch, borrel of vergadering. Wij plannen de sessies zo dat de rest van jullie dag intact blijft.",
      },
    ],
    bullets: sharedBullets,
    faq: sharedFaq,
  },
  {
    slug: "vr-teambuilding-amsterdam",
    city: "Amsterdam",
    h1: "VR teambuilding in Amsterdam",
    title: "VR Teambuilding Amsterdam | Bedrijfsuitje op Locatie – PlayfieldVR",
    description:
      "VR teambuilding in Amsterdam en omgeving: wij bouwen op kantoor of in de zaal. Free-roam, geen gedoe. Vraag een offerte aan.",
    intro:
      "In Amsterdam is ruimte kostbaar en agenda's zijn vol. Daarom komt PlayfieldVR met een complete free-roam VR opstelling naar jullie kantoor of zaal, ergens tussen twee vergaderingen in. Geen locatie zoeken, geen groep die kwijtraakt op de tram.",
    sections: [
      {
        heading: "VR op je eigen kantoor",
        body: "Een leeg kantoordeel, een kantine of een eventruimte is meestal al genoeg. Wij checken vooraf de afmetingen en vloer, en bouwen op zonder in de vloer of muren te hoeven boren.",
      },
      {
        heading: "Voor internationale teams",
        body: "Onze crew begeleidt in Nederlands en Engels, en de games zijn taalonafhankelijk genoeg om met een gemengd team te spelen zonder dat iemand achterblijft.",
      },
      {
        heading: "Regio Amsterdam",
        body: "Wij spelen in Amsterdam en omgeving, waaronder Amstelveen, Diemen, Zaanstad en Purmerend. Een sessie op een avond of in het weekend is bespreekbaar.",
      },
    ],
    bullets: sharedBullets,
    faq: sharedFaq,
  },
];

export const getServicePage = (slug?: string) =>
  servicePages.find((p) => p.slug === slug);
